/**
 * アプリケーション認証フック
 * backend の session cookie を source of truth として認証状態を管理する
 */

import { authApi, type AuthSessionUser } from '@/lib/api/auth'
import { AUTH_UNAUTHORIZED_EVENT } from '@/lib/auth/authEvents'
import type { UserRole } from '@/types/auth'
import { UserRoleValues } from '@/types/auth'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import logger from '@/utils/logger'

export type AppSession = {
  access_token?: string | null
}

export type AppUser = AuthSessionUser & {
  username: string | null
  user_metadata?: {
    name?: string | null
  }
}

interface SupabaseAuthContextType {
  session: AppSession | null
  user: AppUser | null
  isAdmin: boolean
  authError: string | null
  isLoading: boolean
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const SupabaseAuthContext = createContext<SupabaseAuthContextType | undefined>(undefined)

function normalizeRole(role: string | null | undefined): UserRole {
  const normalized = role?.toUpperCase()
  if (normalized === UserRoleValues.ADMIN) {
    return UserRoleValues.ADMIN
  }
  if (normalized === UserRoleValues.MANAGER) {
    return UserRoleValues.MANAGER
  }
  return UserRoleValues.USER
}

function buildAppUser(user: AuthSessionUser): AppUser {
  return {
    ...user,
    role: normalizeRole(user.role),
    username: user.email?.split('@')[0] ?? null,
    user_metadata: {
      name: user.name ?? null,
    },
  }
}

export const SupabaseAuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<AppSession | null>(null)
  const [user, setUser] = useState<AppUser | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isMountedRef = useRef(true)
  const userRef = useRef<AppUser | null>(null)

  useEffect(() => {
    userRef.current = user
  }, [user])

  const clearAuthState = useCallback(() => {
    setSession(null)
    setUser(null)
    setIsAdmin(false)
  }, [])

  const applyAuthenticatedState = useCallback((payload: AuthSessionUser) => {
    const nextUser = buildAppUser(payload)
    setSession({ access_token: null })
    setUser(nextUser)
    setIsAdmin(nextUser.role === UserRoleValues.ADMIN)
    setAuthError(null)
  }, [])

  const loadSession = useCallback(
    async (options?: { showLoading?: boolean }) => {
      if (options?.showLoading) {
        setIsLoading(true)
      }

      try {
        const payload = await authApi.getSession()
        if (!isMountedRef.current) {
          return
        }
        applyAuthenticatedState(payload)
      } catch (error: unknown) {
        if (!isMountedRef.current) {
          return
        }

        // client.ts のインターセプタは AxiosError を ApiClientError に詰め替えて
        // reject するので、後者の error.status と、未詰め替えケースの error.response.status
        // の両方を確認する。
        const status = typeof error === 'object' && error
          ? ((error as { response?: { status?: number } }).response?.status
              ?? (error as { status?: number }).status)
          : undefined

        if (status === 401) {
          clearAuthState()
          setAuthError(null)
          return
        }

        logger.error('[Auth] Failed to resolve current session', error)
        if (!userRef.current) {
          clearAuthState()
        }
        setAuthError('権限情報の取得に失敗しました。再読み込みしてもう一度お試しください。')
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false)
        }
      }
    },
    [applyAuthenticatedState, clearAuthState],
  )

  useEffect(() => {
    isMountedRef.current = true
    void loadSession({ showLoading: true })

    const handleUnauthorized = () => {
      if (!isMountedRef.current) {
        return
      }
      clearAuthState()
      setAuthError(null)
      setIsLoading(false)
    }

    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized)

    return () => {
      isMountedRef.current = false
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized)
    }
  }, [clearAuthState, loadSession])

  const signOut = useCallback(async () => {
    setIsLoading(true)

    try {
      await authApi.logout()
    } catch (error) {
      logger.warn('[Auth] Logout request failed', {
        error: error instanceof Error ? error.message : String(error),
      })
    } finally {
      if (isMountedRef.current) {
        clearAuthState()
        setAuthError(null)
        setIsLoading(false)
      }
    }
  }, [clearAuthState])

  const refreshProfile = useCallback(async () => {
    await loadSession({ showLoading: true })
  }, [loadSession])

  const value = useMemo<SupabaseAuthContextType>(
    () => ({
      session,
      user,
      isAdmin,
      authError,
      isLoading,
      signOut,
      refreshProfile,
    }),
    [authError, isAdmin, isLoading, refreshProfile, session, signOut, user],
  )

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  )
}

export const useSupabaseAuth = (): SupabaseAuthContextType => {
  const context = useContext(SupabaseAuthContext)
  if (context === undefined) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider')
  }
  return context
}
